import '../../App.css';
import { useNavigate } from 'react-router-dom';
import backgroundImage from './assets/background.webp';
import centralImage from './assets/central-image-sem-logo.webp';
import tapToStartImage from './assets/botao_tap_to_start.webp';
import infoImage from './assets/info.webp';
import { useState, useEffect, useRef } from 'react';
import assetPreloader from '../../services/assetPreloader';
import OptimizedImage from '../../components/OptimizedImage';
import { enhanceElementAccessibility } from '../../utils/mobileAccessibility';

function Tela1() {
  const navigate = useNavigate();
  const [position, setPosition] = useState({ y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const lastTapTimeRef = useRef(0);

  // Inicia o precarregamento de assets quando o componente montar
  useEffect(() => {
    // Inicia o carregamento com opção de background para não bloquear a UI
    assetPreloader.preloadAll(() => {
      console.log('Assets de alta prioridade carregados! Restante em segundo plano...');
      setIsLoading(false);
    }, { 
      background: true,  // Carregar média/baixa prioridade em segundo plano
      concurrency: 3     // Número máximo de carregamentos paralelos
    });
  }, []);
  
  // Aplicar melhorias de acessibilidade
  useEffect(() => {
    if (containerRef.current) {
      enhanceElementAccessibility(containerRef.current, {
        role: 'button',
        label: 'Continuar para próxima tela',
        description: 'Arraste para cima ou para baixo, ou toque para continuar'
      });
    }
  }, []);

  // Manipuladores de eventos para mouse
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartPos({ y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const dragDistance = e.clientY - startPos.y;
      // Aplicando resistência - quanto mais longe do centro, menor o movimento
      const resistance = Math.abs(dragDistance) / 100 + 1;
      const newY = dragDistance / resistance;
      setPosition({ y: newY });
    }
  };

  const handleMouseUp = (e) => {
    setIsDragging(false);
    returnToCenter();
    
    // Verificar se foi um clique (não um arrasto)
    if (Math.abs(position.y) < 10) {
      navigateToScreen2();
    }
  };

  // Manipuladores de eventos para toque (dispositivos móveis)
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartPos({ y: e.touches[0].clientY - position.y });
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      const dragDistance = e.touches[0].clientY - startPos.y;
      // Aplicando resistência - quanto mais longe do centro, menor o movimento
      const resistance = Math.abs(dragDistance) / 80 + 1;
      const newY = dragDistance / resistance;
      setPosition({ y: newY });
    }
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    returnToCenter();
    
    // Verificar se foi um tap simples (não um arrasto)
    if (Math.abs(position.y) < 10) {
      const now = new Date().getTime();
      const timeSince = now - lastTapTimeRef.current;
      
      // Evitar duplo tap
      if (timeSince > 300) {
        navigateToScreen2();
      }
      
      lastTapTimeRef.current = now;
    }
  };

  // Navegação para a tela 1-1 (ao invés de tela 2)
  const navigateToScreen2 = () => {
    // Só permitir a navegação se os assets já estiverem carregados
    if (!isLoading) {
      navigate('/tela1-1');
    }
  };

  // Retorna a imagem ao centro com animação rápida de elástico
  const returnToCenter = () => {
    if (imageRef.current) {
      imageRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      
      setTimeout(() => {
        setPosition({ y: 0 });
        
        // Remove a transição após a animação completar
        setTimeout(() => {
          if (imageRef.current) {
            imageRef.current.style.transition = '';
          }
        }, 300);
      }, 10);
    }
  };

  // Adiciona manipuladores de eventos globais
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        returnToCenter();
      }
    };

    // Adiciona listeners globais para garantir que o mouseup seja detectado mesmo fora do componente
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [isDragging]);

  // Calcular o valor de 5% da altura da tela para deslocar a imagem para baixo (metade dos 10% anteriores)
  const offsetY = window.innerHeight * 0.05;

  return (
    <div className="App">
      <div className="background-container">
        <OptimizedImage 
          src={backgroundImage} 
          className="background-image" 
          alt="Background" 
          loading="eager"
          responsive={false}
          fullWidth={true}
        />
      </div>
      <div 
        ref={containerRef}
        className="central-image-container interactive-element"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        tabIndex={0}
        style={{ zIndex: 10 }}
      >
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transform: `translateY(calc(${position.y}px + ${offsetY}px))`,
          paddingTop: '5vh'
        }}>
          <OptimizedImage 
            ref={imageRef}
            src={centralImage} 
            className="central-image" 
            alt="Central" 
            style={{ 
              cursor: isDragging ? 'grabbing' : 'grab',
              position: 'relative',
              maxHeight: '70vh',
              maxWidth: '90%',
              objectFit: 'contain',
              marginTop: '100px'
            }}
            loading="eager"
            responsive={true}
          />
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            marginTop: '20px'
          }}>
            <OptimizedImage 
              src={tapToStartImage}
              className="tap-to-start-button"
              alt="Tap to Start"
              onClick={navigateToScreen2}
              style={{
                cursor: 'pointer',
                maxWidth: '200px',
                objectFit: 'contain'
              }}
              loading="eager"
              responsive={true}
            />
            <OptimizedImage 
              src={infoImage}
              className="info-image"
              alt="Information"
              style={{
                maxWidth: '150px',
                marginTop: '20px',
                objectFit: 'contain'
              }}
              loading="eager"
              responsive={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tela1; 